import React, { useState, useEffect } from "react";
import Head from "../layout/head/Head";
import Content from "../layout/content/Content";
import SaleRevenue from "../components/partials/default/sale-revenue/SaleRevenue";
import SalesOverview from "../components/partials/default/sales-overview/SalesOverview";
import TransactionTable from "../components/partials/default/transaction/Transaction";
import { DropdownToggle, DropdownMenu, Card, UncontrolledDropdown, DropdownItem } from "reactstrap";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Button,
  Row,
  Col,
  PreviewCard,
  PreviewAltCard,
  BlockBetween,
} from "../components/Component";
import { handleGetApis } from "../service/Api";

// ─── Filter Config ────────────────────────────────────────────────────────────

const FILTERS = [
  { key: "30days",   label: "Last 30 Days",  days: 30  },
  { key: "3weeks",   label: "Last 3 Weeks",  days: 21  },
  { key: "6months",  label: "Last 6 Months", days: 180 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getRevenueByRange = (orders, startDate, endDate) =>
  orders
    .filter((o) => {
      const d = new Date(o.created_at);
      return d >= startDate && d <= endDate;
    })
    .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

const getFilteredOrders = (orders, days) => {
  const now   = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - days);
  start.setHours(0, 0, 0, 0);
  return orders.filter((o) => new Date(o.created_at) >= start);
};

/** Bar chart — monthly revenue for current year (always full year) */
const buildBarChartData = (orders) => {
  const months       = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const currentYear  = new Date().getFullYear();
  const monthlyTotals = new Array(12).fill(0);

  orders.forEach((o) => {
    const d = new Date(o.created_at);
    if (d.getFullYear() === currentYear) {
      monthlyTotals[d.getMonth()] += parseFloat(o.total || 0);
    }
  });

  const currentMonth = new Date().getMonth();
  const bgColors = monthlyTotals.map((_, i) =>
    i === currentMonth ? "rgba(101,118,255,1)" : "rgba(101,118,255,0.2)"
  );

  return {
    labels: months,
    dataUnit: "INR",
    stacked: true,
    datasets: [{
      label: "Sales Revenue",
      barPercentage: 0.7,
      categoryPercentage: 0.7,
      backgroundColor: bgColors,
      data: monthlyTotals,
    }],
  };
};

/** Line chart — daily revenue for N days (30days/3weeks) or weekly for 6months */
const buildLineChartData = (orders, filterKey) => {
  const today  = new Date();
  const labels = [];
  const values = [];

  if (filterKey === "6months") {
    // Weekly aggregation — last 26 weeks
    for (let i = 25; i >= 0; i--) {
      const weekEnd   = new Date(today);
      weekEnd.setDate(today.getDate() - i * 7);
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekEnd.getDate() - 6);
      weekStart.setHours(0, 0, 0, 0);
      weekEnd.setHours(23, 59, 59, 999);

      const total = orders
        .filter((o) => {
          const d = new Date(o.created_at);
          return d >= weekStart && d <= weekEnd;
        })
        .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

      // Label: day-month of week start
      const d = weekStart;
      labels.push(`${String(d.getDate()).padStart(2,"0")} ${d.toLocaleString("en",{month:"short"})}`);
      values.push(total);
    }
  } else {
    // Daily aggregation
    const days = filterKey === "3weeks" ? 21 : 30;
    for (let i = days - 1; i >= 0; i--) {
      const d        = new Date(today);
      d.setDate(today.getDate() - i);
      const dayStart = startOfDay(d);
      const dayEnd   = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);

      const total = orders
        .filter((o) => {
          const od = new Date(o.created_at);
          return od >= dayStart && od <= dayEnd;
        })
        .reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

      labels.push(String(d.getDate()).padStart(2, "0"));
      values.push(total);
    }
  }

  return {
    labels,
    dataUnit: "INR",
    datasets: [{
      label: "Sales Overview",
      color: "#798bff",
      fill: true,
      backgroundColor: "rgba(101,118,255,0.25)",
      borderColor: "#798bff",
      borderWidth: 2,
      lineTension: 0.1,
      pointBorderColor: "transparent",
      pointBackgroundColor: "transparent",
      pointHoverBorderColor: "#798bff",
      pointHoverBackgroundColor: "#fff",
      data: values,
    }],
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

const Homepage = () => {
  const [sm, updateSm]         = useState(false);
  const [orders, setOrders]    = useState([]);
  const [loading, setLoading]  = useState(true);
  const [activeFilter, setActiveFilter] = useState(FILTERS[0]); // default: Last 30 Days

  // Dashboard computed stats
  const [thisMonthRevenue, setThisMonthRevenue] = useState(undefined);
  const [thisWeekRevenue,  setThisWeekRevenue]  = useState(undefined);
  const [monthChange,      setMonthChange]      = useState(undefined);
  const [weekChange,       setWeekChange]       = useState(undefined);
  const [filteredRevenue,  setFilteredRevenue]  = useState(undefined);
  const [filteredOrders,   setFilteredOrders]   = useState([]);
  const [barChartData,     setBarChartData]     = useState(null);
  const [lineChartData,    setLineChartData]    = useState(null);

  // Fetch once on mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const res = await handleGetApis("admin/get/orders");
        if (res?.data?.success) {
          const allOrders = res.data.data;
          setOrders(allOrders);
          computeStats(allOrders, FILTERS[0]);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  // Re-compute when filter changes (orders already loaded)
  useEffect(() => {
    if (orders.length > 0) {
      computeStats(orders, activeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const computeStats = (allOrders, filter) => {
    const now = new Date();

    // ── This Month ────────────────────────────────────────
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth  = getRevenueByRange(allOrders, monthStart, now);

    // ── Last Month ────────────────────────────────────────
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    const lastMonth      = getRevenueByRange(allOrders, lastMonthStart, lastMonthEnd);

    // ── This Week ─────────────────────────────────────────
    const weekStart = startOfDay(new Date(now));
    weekStart.setDate(now.getDate() - now.getDay());
    const thisWeek  = getRevenueByRange(allOrders, weekStart, now);

    // ── Last Week ─────────────────────────────────────────
    const lastWeekEnd   = new Date(weekStart);
    lastWeekEnd.setMilliseconds(-1);
    const lastWeekStart = new Date(lastWeekEnd);
    lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
    lastWeekStart.setHours(0, 0, 0, 0);
    const lastWeek      = getRevenueByRange(allOrders, lastWeekStart, lastWeekEnd);

    // ── % Changes ─────────────────────────────────────────
    const mChange = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : null;
    const wChange = lastWeek  > 0 ? ((thisWeek  - lastWeek)  / lastWeek)  * 100 : null;

    // ── Filtered Data (by active date filter) ─────────────
    const inRange    = getFilteredOrders(allOrders, filter.days);
    const rangeTotal = inRange.reduce((sum, o) => sum + parseFloat(o.total || 0), 0);

    setThisMonthRevenue(thisMonth.toFixed(2));
    setThisWeekRevenue(thisWeek.toFixed(2));
    setMonthChange(mChange);
    setWeekChange(wChange);
    setFilteredRevenue(rangeTotal.toFixed(2));
    setFilteredOrders(inRange);
    setBarChartData(buildBarChartData(allOrders));
    setLineChartData(buildLineChartData(allOrders, filter.key));
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <>
      <Head title="Homepage"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">Sales Overview</BlockTitle>
              <BlockDes className="text-soft">
                <p>Welcome to ryzhm Dashboard</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <Button
                  className={`btn-icon btn-trigger toggle-expand me-n1 ${sm ? "active" : ""}`}
                  onClick={() => updateSm(!sm)}
                >
                  <Icon name="more-v" />
                </Button>
                <div className="toggle-expand-content" style={{ display: sm ? "block" : "none" }}>
                  <ul className="nk-block-tools g-3">
                    <li>
                      <UncontrolledDropdown>
                        <DropdownToggle tag="a" className="dropdown-toggle btn btn-white btn-dim btn-outline-light">
                          <Icon className="d-none d-sm-inline" name="calender-date" />
                          <span>
                            <span className="d-none d-md-inline">{activeFilter.label}</span>
                            <span className="d-md-none">Filter</span>
                          </span>
                          <Icon className="dd-indc" name="chevron-right" />
                        </DropdownToggle>
                        <DropdownMenu>
                          <ul className="link-list-opt no-bdr">
                            {FILTERS.map((f) => (
                              <li key={f.key} className={activeFilter.key === f.key ? "active" : ""}>
                                <DropdownItem
                                  tag="a"
                                  href={`#${f.key}`}
                                  onClick={(ev) => {
                                    ev.preventDefault();
                                    handleFilterChange(f);
                                  }}
                                >
                                  <span>{f.label}</span>
                                </DropdownItem>
                              </li>
                            ))}
                          </ul>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            <Col xxl="12">
              <Row className="g-gs">
                <Col lg="12" xxl="12">
                  <PreviewCard>
                    <SaleRevenue
                      thisMonth={thisMonthRevenue}
                      thisWeek={thisWeekRevenue}
                      monthChange={monthChange}
                      weekChange={weekChange}
                      barChartData={barChartData}
                    />
                  </PreviewCard>
                </Col>
              </Row>
            </Col>
            <Col xxl="6">
              <PreviewAltCard className="h-100">
                <SalesOverview
                  totalRevenue={filteredRevenue}
                  totalOrders={filteredOrders.length}
                  lineChartData={lineChartData}
                  orders={filteredOrders}
                  filterLabel={activeFilter.label}
                />
              </PreviewAltCard>
            </Col>
            <Col xxl="8">
              <Card className="card-bordered card-full">
                <TransactionTable orders={orders} />
              </Card>
            </Col>
          </Row>
        </Block>
      </Content>
    </>
  );
};
export default Homepage;
